import { AssetRow, NamirasoftBalanceServer } from "namirasoft-balance";
import { MoneyFormatter, NSLoading, NSNoData } from "namirasoft-site-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BalanceRoutes } from "../BalanceRoutes";
import Styles from "./AssetBalances.module.css";

interface AssetBalancesProps
{
    server: NamirasoftBalanceServer;
}

interface BalanceItem
{
    asset: AssetRow;
    total_debit: number;
    total_credit: number;
    total_balance: number;
    count: number;
    min_date: Date | string | null;
    max_date: Date | string | null;
}

function formatAmount(assetName: string, value: number): string
{
    return new MoneyFormatter(undefined, assetName).format(value);
}

function formatDate(value: Date | string | null): string
{
    if (value === null) return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toISOString().slice(0, 10);
}

export function AssetBalances(props: AssetBalancesProps)
{
    const navigate = useNavigate();
    const [rows, setRows] = useState<BalanceItem[] | null>(null);

    useEffect(() =>
    {
        let cancelled = false;
        props.server._get<{ rows: BalanceItem[], count: number }>(
            "/transaction/balance",
            { asset_id: null }
        ).then(res =>
        {
            if (cancelled) return;
            setRows(res.data.rows);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, []);

    if (rows === null)
        return <NSLoading title="Loading balances..." />

    if (rows.length === 0)
        return <NSNoData lable="No assets available yet." />

    const sorted = [...rows].sort((a, b) => (a.asset.name ?? "").localeCompare(b.asset.name ?? ""));

    return (
        <div className={Styles.balances_container}>
            {sorted.map(row =>
            {
                const assetName = row.asset.name ?? "";
                const balanceClass = row.total_balance > 0
                    ? Styles.balance_value_positive
                    : row.total_balance < 0
                        ? Styles.balance_value_negative
                        : "";
                const debits = row.total_debit;
                const credits = Math.abs(row.total_credit);
                return (
                    <div key={row.asset.id} className={Styles.card}>
                        <div className={Styles.header}>
                            <div className={Styles.header_left}>
                                <div className={Styles.asset_name}>{assetName || "Asset"}</div>
                                <div className={Styles.asset_type}>{row.asset.type}</div>
                            </div>
                            <div className={Styles.header_right}>
                                <div className={Styles.balance_label}>Balance</div>
                                <div className={`${Styles.balance_value} ${balanceClass}`}>
                                    {formatAmount(assetName, row.total_balance)}
                                </div>
                            </div>
                        </div>
                        <div className={Styles.body}>
                            <div className={Styles.cell}>
                                <div className={Styles.cell_label}>
                                    <span className={`${Styles.badge} ${Styles.badge_up}`}>+</span>
                                    Debits
                                </div>
                                <div className={`${Styles.cell_value} ${Styles.cell_value_credit}`}>
                                    {formatAmount(assetName, debits)}
                                </div>
                            </div>
                            <div className={Styles.cell}>
                                <div className={Styles.cell_label}>
                                    <span className={`${Styles.badge} ${Styles.badge_down}`}>−</span>
                                    Credits
                                </div>
                                <div className={`${Styles.cell_value} ${Styles.cell_value_debit}`}>
                                    {formatAmount(assetName, credits)}
                                </div>
                            </div>
                            <div className={Styles.cell}>
                                <div className={Styles.cell_label}>Transactions</div>
                                <div className={Styles.cell_value}>{row.count}</div>
                            </div>
                            <div className={Styles.cell}>
                                <div className={Styles.cell_label}>Last activity</div>
                                <div className={`${Styles.cell_value} ${Styles.cell_value_muted}`}>
                                    {formatDate(row.max_date)}
                                </div>
                            </div>
                        </div>
                        <div className={Styles.actions}>
                            <button
                                type="button"
                                className={`${Styles.action_button} ${Styles.action_button_primary}`}
                                onClick={() => navigate(BalanceRoutes.TransactionList(row.asset.id))}
                            >
                                Transactions
                            </button>
                            <button
                                type="button"
                                className={`${Styles.action_button} ${Styles.action_button_secondary}`}
                                onClick={() => navigate(BalanceRoutes.SummaryList(row.asset.id))}
                            >
                                Summary
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}